import {makeAutoObservable, observable, runInAction} from 'mobx';
import {collaboratorsByCompany, Company} from "../model/professional/compagny-model";
import professionalStore from './professional-store';
export class CompanyStore {
    companies: Company[] = []
    @observable isLoading: boolean = false
    @observable idCompanySelectedDefault: string | null = null
    @observable selectedCompany: Company | null = null

    private static instance: CompanyStore;

    private constructor() {
        makeAutoObservable(this);
    }

    public static getInstance(): CompanyStore {
        if (!CompanyStore.instance) {
            CompanyStore.instance = new CompanyStore();
        }
        return CompanyStore.instance;
    }

    setIsLoading(){
        this.isLoading = true
    }

    setCompanies(companies: Company[]) {
        runInAction(() => {
            this.companies = companies
            this.setIsLoading()
        })
    }

    setIdCompanySelectedDefault(idCompanySelectedDefault: string) {
        this.idCompanySelectedDefault = idCompanySelectedDefault

    }

    get getCompanies() {
        return this.companies
    }

    get getCollaboratorsByCompany() {
        return this.companies
    }

    get isNoCompanies() {
        return this.companies.length <= 0
    }

    /**
     * Vérifie si l'utilisateur n'est owner d'aucune entreprise
     * (même s'il est membre collaborateur d'entreprises)
     * @returns true si l'utilisateur n'est owner d'aucune entreprise
     */
    get hasNoOwnedCompanies(): boolean {
        const currentUserId = professionalStore.getId;

        if (!currentUserId || this.companies.length === 0) {
            return true;
        }

        // Vérifier si l'utilisateur est owner d'au moins une entreprise
        return !this.companies.some(company => this.isUserOwnerOfCompany(company));
    }

    get getIdCompanySelectedDefault(){
        return this.idCompanySelectedDefault
    }
    
    /**
     * Définit la compagnie sélectionnée par son ID
     * @param id - L'ID de la compagnie à sélectionner
     */
    setCompanyById = (id: string | null): void => {
        // Vérifier que l'ID n'est pas vide et que companies est initialisé
        if (!id || !this.companies) {
            console.warn("ID invalide ou companies non initialisé", { id, hasCompanies: !!this.companies });
            return;
        }
        
        // Utiliser runInAction pour modifier des états observables
        runInAction(() => {
            // Trouver la compagnie correspondante
            const found = this.companies.find(company => company.id === id);
            
            if (found) {
                this.selectedCompany = found;
            } else {
                this.selectedCompany = null;
            }
        });
    }

    get getCompanyById() {
        return this.selectedCompany
    }

    /**
     * Récupère la company sélectionnée par défaut (celle actuellement active)
     * @returns La company par défaut ou null
     */
    get getDefaultCompany(): Company | null {
        if (!this.idCompanySelectedDefault || this.companies.length === 0) {
            return null;
        }
        return this.companies.find(c => c.id === this.idCompanySelectedDefault) || null;
    }

    /**
     * Récupère les collaborateurs de la company par défaut
     * @returns Liste des collaborateurs ou tableau vide
     */
    get getDefaultCompanyCollaborators(): collaboratorsByCompany[] {
        const company = this.getDefaultCompany;
        return company?.collaboratorCompanies || [];
    }

    /**
     * Met à jour une entreprise dans le store
     * @param updatedCompany - Les données mises à jour de l'entreprise
     */
    updateCompany = (updatedCompany: Partial<Company> & { id: string }): void => {
        runInAction(() => {
            const index = this.companies.findIndex(company => company.id === updatedCompany.id);
            if (index !== -1) {
                this.companies[index] = {
                    ...this.companies[index],
                    ...updatedCompany
                };

                if (this.selectedCompany?.id === updatedCompany.id) {
                    this.selectedCompany = this.companies[index];
                }
            }
        });
    }

    /**
     * Vérifie si l'utilisateur actuel est admin de l'entreprise sélectionnée
     * @returns true si l'utilisateur est admin, false sinon
     */
    get isCurrentUserAdmin(): boolean {
        const currentUserId = professionalStore.getId;

        if (!currentUserId || !this.selectedCompany?.collaboratorCompanies) {
            return false;
        }

        const collaborator = this.selectedCompany.collaboratorCompanies.find(
            c => c.collaboratorId === currentUserId
        );

        return collaborator?.role === 'ADMIN' || collaborator?.role === 'OWNER';
    }


    /**
     * Vérifie si l'utilisateur actuel est owner d'une entreprise spécifique
     * @param company - L'entreprise à vérifier
     * @returns true si l'utilisateur est owner, false sinon
     */
    isUserOwnerOfCompany(company: Company): boolean {
        const currentUserId = professionalStore.getId;

        if (!currentUserId || !company?.collaboratorCompanies) {
            return false;
        }

        const collaborator = company.collaboratorCompanies.find(
            c => c.collaboratorId === currentUserId
        );

        return collaborator?.owner === true;
    }
}

export default CompanyStore.getInstance()